import { computed, inject, Injectable, signal } from '@angular/core';

import { NavItem } from '../models/navigation.model';
import { AuthService } from './auth.service';
import { MENU } from '../constants/menu';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly authService = inject(AuthService);

  // Use the compiled-in MENU constant so the app doesn't fetch JSON at runtime.
  private readonly rawMenu = signal<readonly NavItem[]>(MENU);

  /**
   * Reactive list of menu items visible to the current user.
   * Recomputes automatically when the user signal changes (login / logout).
   */
  readonly filteredMenu = computed(() => {
    // Accessing user() here makes this computed reactive to auth state.
    void this.authService.user();
    return this.filterByAccess(this.rawMenu());
  });

  constructor() {
    // MENU is provided at build time via src/app/core/constants/menu.ts
    // No runtime HTTP request required.
  }

  private filterByAccess(items: readonly NavItem[]): NavItem[] {
    const result: NavItem[] = [];

    for (const item of items) {
      if (item.visibility === false) {
        continue;
      }

      if (!this.isModuleAccessible(item)) {
        continue;
      }

      if (!this.isPermissionGranted(item)) {
        continue;
      }

      if (item.children?.length) {
        const visibleChildren = this.filterByAccess(item.children);
        if (visibleChildren.length === 0) {
          continue;
        }
        result.push({ ...item, children: this.sortByPriority(visibleChildren) });
      } else {
        result.push(item);
      }
    }

    return this.sortByPriority(result);
  }

  private isModuleAccessible(item: NavItem): boolean {
    if (item.moduleId === undefined) {
      return true;
    }
    return this.authService.hasModule(item.moduleId);
  }

  private isPermissionGranted(item: NavItem): boolean {
    if (item.permissionId === undefined || item.permissionId === null) {
      return true;
    }
    return this.authService.hasAnyPermission([item.permissionId]);
  }

  private sortByPriority(items: NavItem[]): NavItem[] {
    return [...items].sort((a, b) => {
      const pa = a.priority ?? Number.MAX_SAFE_INTEGER;
      const pb = b.priority ?? Number.MAX_SAFE_INTEGER;
      return pa - pb;
    });
  }
}
